import { useState, useRef, useEffect, useCallback } from 'react'
import type { PlaybookLabel } from '@/types/Label.types'
import './label-popover.css'

interface LabelFilterPopoverProps {
	anchorRef: React.RefObject<HTMLElement | null>
	labels: PlaybookLabel[]
	counts: Record<string, number>
	selectedIds: string[]
	onSelectionChange: (ids: string[]) => void
	onClose: () => void
}

export function LabelFilterPopover({
	anchorRef,
	labels,
	counts,
	selectedIds,
	onSelectionChange,
	onClose,
}: LabelFilterPopoverProps) {
	const [search, setSearch] = useState('')
	const popoverRef = useRef<HTMLDivElement>(null)

	const handleClose = useCallback(() => onClose(), [onClose])

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				popoverRef.current &&
				!popoverRef.current.contains(e.target as Node) &&
				!anchorRef.current?.contains(e.target as Node)
			) {
				handleClose()
			}
		}
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') handleClose()
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [handleClose, anchorRef])

	const filtered = labels.filter((l) =>
		l.name.toLowerCase().includes(search.toLowerCase()),
	)

	const handleToggle = (labelId: string) => {
		if (selectedIds.includes(labelId)) {
			onSelectionChange(selectedIds.filter((id) => id !== labelId))
		} else {
			onSelectionChange([...selectedIds, labelId])
		}
	}

	return (
		<div ref={popoverRef} className="label-popover label-assign-popover">
			<input
				type="text"
				placeholder="Filter labels..."
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				className="label-create-input"
				autoFocus
			/>

			<div className="label-assign-list">
				{filtered.map((label) => (
					<label key={label.id} className="label-assign-row" aria-label={label.name}>
						<input
							type="checkbox"
							checked={selectedIds.includes(label.id)}
							onChange={() => handleToggle(label.id)}
						/>
						<span
							className="label-assign-dot"
							style={{ backgroundColor: label.color }}
						/>
						<span className="label-assign-name">{label.name}</span>
						<span className="label-filter-pill-count">{counts[label.id] || 0}</span>
					</label>
				))}
			</div>

			{selectedIds.length > 0 && (
				<div className="label-filter-popover-actions">
					<button
						className="label-filter-clear"
						onClick={() => onSelectionChange([])}
					>
						Clear all
					</button>
				</div>
			)}
		</div>
	)
}
